/**
 * entry-fees-ui.ts - Entry fee management UI
 *
 * Handles entry fee creation, editing, and deletion for tournament organizers
 */

import {
  getEntryFees,
  createEntryFee,
  updateEntryFee,
  deleteEntryFee,
  type EntryFee
} from '../api/registration-api';
import { escapeHtml } from '../utils/security';
import { t } from '../utils/i18n-helper';
import { API_BASE_URL } from '../utils/constants';
import type { Player } from '../../types/api';

// Fee percentages
const STRIPE_FEE_PERCENT = 0.0255; // 2.55%
const NEXTROUND_FEE_PERCENT = 0.0245; // 2.45%

// Preferred currencies for tie-breaking (in order of preference)
const PREFERRED_CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CHF'];

// Supported currencies for entry fees (same as manual payment)
const SUPPORTED_CURRENCIES = [
  // Europe
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'PLN', name: 'Polish Zloty' },
  { code: 'CZK', name: 'Czech Koruna' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'HUF', name: 'Hungarian Forint' },
  { code: 'RON', name: 'Romanian Leu' },
  { code: 'BGN', name: 'Bulgarian Lev' },
  { code: 'RSD', name: 'Serbian Dinar' },
  { code: 'UAH', name: 'Ukrainian Hryvnia' },
  { code: 'RUB', name: 'Russian Ruble' },
  { code: 'TRY', name: 'Turkish Lira' },
  // Americas
  { code: 'USD', name: 'US Dollar' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'ARS', name: 'Argentine Peso' },
  // Asia
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'KRW', name: 'Korean Won' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'BDT', name: 'Bangladeshi Taka' },
  // Middle East
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'AED', name: 'UAE Dirham' },
  { code: 'EGP', name: 'Egyptian Pound' },
];

// Store current tournament ID for payment summary modal
let currentTournamentIdForSummary: string | null = null;

// Cache for exchange rates (refreshed every hour)
let exchangeRateCache: { rates: Record<string, number>; base: string; timestamp: number } | null = null;
const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour

interface PaymentSummary {
  stripeGross: number;
  stripeFees: number;
  nextroundFees: number;
  stripeNet: number;
  stripeCount: number;
  manualTotal: number;
  manualCount: number;
  grandTotal: number;
  currency: string;
  convertedFrom?: string[]; // List of currencies that were converted
}

/**
 * Format currency amount
 */
function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase()
  }).format(amount / 100);
}

/**
 * Fetch exchange rates from Frankfurter API (free, no API key required)
 * Falls back to a secondary service if needed
 */
async function fetchExchangeRates(baseCurrency: string): Promise<Record<string, number>> {
  // Check cache
  if (exchangeRateCache &&
      exchangeRateCache.base === baseCurrency &&
      Date.now() - exchangeRateCache.timestamp < CACHE_DURATION_MS) {
    return exchangeRateCache.rates;
  }

  try {
    // Try Frankfurter API first (free, reliable, European Central Bank data)
    const response = await fetch(`https://api.frankfurter.app/latest?from=${baseCurrency}`);
    if (response.ok) {
      const data = await response.json();
      const rates: Record<string, number> = { [baseCurrency]: 1, ...data.rates };
      exchangeRateCache = { rates, base: baseCurrency, timestamp: Date.now() };
      return rates;
    }
  } catch (error) {
    console.warn('Frankfurter API failed, trying fallback:', error);
  }

  try {
    // Fallback to exchangerate.host (also free)
    const response = await fetch(`https://api.exchangerate.host/latest?base=${baseCurrency}`);
    if (response.ok) {
      const data = await response.json();
      if (data.success !== false && data.rates) {
        const rates: Record<string, number> = { [baseCurrency]: 1, ...data.rates };
        exchangeRateCache = { rates, base: baseCurrency, timestamp: Date.now() };
        return rates;
      }
    }
  } catch (error) {
    console.warn('Fallback exchange rate API also failed:', error);
  }

  // Return empty rates if all APIs fail
  return { [baseCurrency]: 1 };
}

/**
 * Convert amount from one currency to another
 */
function convertAmount(amount: number, fromCurrency: string, toCurrency: string, rates: Record<string, number>): number {
  if (fromCurrency === toCurrency) return amount;

  // rates are relative to the base currency (toCurrency)
  // If we have rate for fromCurrency, we divide by it
  const fromRate = rates[fromCurrency];
  if (!fromRate) {
    console.warn(`No exchange rate found for ${fromCurrency}, using 1:1`);
    return amount;
  }

  // amount in fromCurrency / fromRate = amount in baseCurrency (toCurrency)
  return Math.round(amount / fromRate);
}

/**
 * Determine target currency based on payment counts
 * Uses the currency with most payments, with tie-breaker preference for major currencies
 */
function determineTargetCurrency(currencyCounts: Record<string, number>): string {
  const currencies = Object.keys(currencyCounts);
  if (currencies.length === 0) return 'USD';
  if (currencies.length === 1) return currencies[0];

  // Find the maximum count
  const maxCount = Math.max(...Object.values(currencyCounts));

  // Get all currencies with the maximum count
  const topCurrencies = currencies.filter(c => currencyCounts[c] === maxCount);

  if (topCurrencies.length === 1) {
    return topCurrencies[0];
  }

  // Tie-breaker: prefer currencies in order of PREFERRED_CURRENCIES
  for (const preferred of PREFERRED_CURRENCIES) {
    if (topCurrencies.includes(preferred)) {
      return preferred;
    }
  }

  // If none of the preferred currencies, use the first one alphabetically
  return topCurrencies.sort()[0];
}

/**
 * Calculate payment summary from players with currency conversion
 */
async function calculatePaymentSummary(players: Player[]): Promise<PaymentSummary> {
  // First pass: count currencies and collect payments
  const currencyCounts: Record<string, number> = {};
  const payments: Array<{ amount: number; currency: string; source: 'stripe' | 'manual' }> = [];

  for (const player of players) {
    if (player.feePaid && player.paidAmount) {
      const currency = player.paidCurrency || 'USD';
      currencyCounts[currency] = (currencyCounts[currency] || 0) + 1;
      payments.push({
        amount: player.paidAmount,
        currency,
        source: player.paymentSource === 'stripe' ? 'stripe' : 'manual'
      });
    }
  }

  // Determine target currency
  const targetCurrency = determineTargetCurrency(currencyCounts);

  // Get unique currencies that need conversion
  const uniqueCurrencies = Object.keys(currencyCounts);
  const needsConversion = uniqueCurrencies.some(c => c !== targetCurrency);
  const convertedFrom = uniqueCurrencies.filter(c => c !== targetCurrency);

  // Fetch exchange rates if needed
  let rates: Record<string, number> = { [targetCurrency]: 1 };
  if (needsConversion) {
    rates = await fetchExchangeRates(targetCurrency);
  }

  // Calculate totals with conversion
  let stripeGross = 0;
  let stripeCount = 0;
  let manualTotal = 0;
  let manualCount = 0;

  for (const payment of payments) {
    const convertedAmount = convertAmount(payment.amount, payment.currency, targetCurrency, rates);

    if (payment.source === 'stripe') {
      stripeGross += convertedAmount;
      stripeCount++;
    } else {
      manualTotal += convertedAmount;
      manualCount++;
    }
  }

  const stripeFees = Math.round(stripeGross * STRIPE_FEE_PERCENT);
  const nextroundFees = Math.round(stripeGross * NEXTROUND_FEE_PERCENT);
  const stripeNet = stripeGross - stripeFees - nextroundFees;
  const grandTotal = stripeNet + manualTotal;

  return {
    stripeGross,
    stripeFees,
    nextroundFees,
    stripeNet,
    stripeCount,
    manualTotal,
    manualCount,
    grandTotal,
    currency: targetCurrency,
    convertedFrom: convertedFrom.length > 0 ? convertedFrom : undefined
  };
}

/**
 * Render payment summary HTML
 */
function renderPaymentSummary(summary: PaymentSummary): string {
  const hasPayments = summary.stripeCount > 0 || summary.manualCount > 0;

  if (!hasPayments) {
    return `
      <div class="payment-summary-section">
        <p style="color: #6b7280; font-style: italic; margin: 0;">
          ${t('entryFees.noPayments', 'No payments recorded yet.')}
        </p>
      </div>
    `;
  }

  const c = summary.currency;

  return `
    <div class="payment-summary-section" style="background: #f9fafb; border-radius: 8px; padding: 16px;">
      ${summary.stripeCount > 0 ? `
        <div style="margin-bottom: 16px; padding-bottom: 16px; border-bottom: 1px solid #e5e7eb;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <span style="font-weight: 600; color: #374151;">
              💳 ${t('entryFees.stripePayments', 'Stripe Payments')}
              <span style="font-weight: 400; color: #6b7280;">(${summary.stripeCount} ${summary.stripeCount === 1 ? t('entryFees.payment', 'payment') : t('entryFees.payments', 'payments')})</span>
            </span>
          </div>
          <div style="display: flex; flex-direction: column; gap: 6px; font-size: 0.9rem;">
            <div style="display: flex; justify-content: space-between;">
              <span style="color: #6b7280;">${t('entryFees.grossAmount', 'Gross amount')}</span>
              <span style="color: #374151;">${formatCurrency(summary.stripeGross, c)}</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="color: #dc2626;">− ${t('entryFees.stripeFees', 'Stripe fees')} (2.55%)</span>
              <span style="color: #dc2626;">-${formatCurrency(summary.stripeFees, c)}</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="color: #dc2626;">− ${t('entryFees.nextroundFees', 'nextround fees')} (2.45%)</span>
              <span style="color: #dc2626;">-${formatCurrency(summary.nextroundFees, c)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding-top: 6px; border-top: 1px dashed #d1d5db;">
              <span style="font-weight: 500; color: #374151;">${t('entryFees.netAmount', 'Net amount')}</span>
              <span style="font-weight: 600; color: #059669;">${formatCurrency(summary.stripeNet, c)}</span>
            </div>
          </div>
        </div>
      ` : ''}

      ${summary.manualCount > 0 ? `
        <div style="margin-bottom: 16px; ${summary.stripeCount > 0 ? '' : 'padding-bottom: 16px; border-bottom: 1px solid #e5e7eb;'}">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-weight: 600; color: #374151;">
              💵 ${t('entryFees.manualPayments', 'Manual/Cash Payments')}
              <span style="font-weight: 400; color: #6b7280;">(${summary.manualCount} ${summary.manualCount === 1 ? t('entryFees.payment', 'payment') : t('entryFees.payments', 'payments')})</span>
            </span>
            <span style="font-weight: 600; color: #059669;">${formatCurrency(summary.manualTotal, c)}</span>
          </div>
        </div>
      ` : ''}

      <div style="display: flex; justify-content: space-between; align-items: center; padding-top: 12px; border-top: 2px solid #374151;">
        <span style="font-weight: 700; font-size: 1.1rem; color: #111827;">
          ${t('entryFees.grandTotal', 'Grand Total')}
        </span>
        <span style="font-weight: 700; font-size: 1.1rem; color: #059669;">
          ${formatCurrency(summary.grandTotal, c)}
        </span>
      </div>

      ${summary.convertedFrom && summary.convertedFrom.length > 0 ? `
        <div style="margin-top: 12px; padding: 10px; background: #fef3c7; border-radius: 6px; font-size: 0.85rem; color: #92400e;">
          💱 ${t('entryFees.currencyConversionNote', 'Amounts converted from')} ${summary.convertedFrom.join(', ')} → ${c}
          <span style="display: block; margin-top: 4px; font-size: 0.8rem; color: #a16207;">
            ${t('entryFees.exchangeRateNote', 'Exchange rates from European Central Bank')}
          </span>
        </div>
      ` : ''}
    </div>
  `;
}

/**
 * Open entry fees modal
 * @param tournamentId - Tournament ID
 */
export async function openEntryFeesModal(tournamentId: string): Promise<void> {
  try {
    // Fetch fees and tournament data in parallel
    const [fees, tournamentResponse] = await Promise.all([
      getEntryFees(tournamentId),
      fetch(`${API_BASE_URL}/${tournamentId}`, { credentials: 'include' })
    ]);

    let paymentSummary: PaymentSummary | null = null;
    if (tournamentResponse.ok) {
      const tournament = await tournamentResponse.json();
      if (tournament.players && tournament.players.length > 0) {
        paymentSummary = await calculatePaymentSummary(tournament.players);
      }
    }

    showEntryFeesModal(tournamentId, fees, paymentSummary);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load entry fees';
    showError(message);
  }
}

/**
 * Show entry fees modal
 */
function showEntryFeesModal(tournamentId: string, fees: EntryFee[], paymentSummary?: PaymentSummary | null): void {
  const modal = document.getElementById('entry-fees-modal');
  if (!modal) {
    createEntryFeesModal();
    return showEntryFeesModal(tournamentId, fees, paymentSummary);
  }

  // Store tournament ID for payment summary modal
  currentTournamentIdForSummary = tournamentId;

  // Update modal content
  const listEl = modal.querySelector('#entry-fees-list');
  if (listEl) {
    listEl.innerHTML = renderEntryFeesList(tournamentId, fees);
    // Apply translations to dynamically updated content
    if (window.i18n && typeof window.i18n.applyTranslations === 'function') {
      // Use setTimeout to ensure DOM is fully updated
      setTimeout(() => window.i18n.applyTranslations(), 0);
    }
  }

  // Set up add button handler
  const addBtn = modal.querySelector('#add-entry-fee-btn') as HTMLButtonElement;
  if (addBtn) {
    addBtn.onclick = () => showEntryFeeForm(tournamentId);
  }

  // Show modal
  modal.style.display = 'flex';
}

/**
 * Render entry fees list
 */
function renderEntryFeesList(tournamentId: string, fees: EntryFee[]): string {
  if (fees.length === 0) {
    return '<p class="empty-state" data-i18n="tournament.entryFeesModal.noFees">No entry fees configured. Click &quot;Add Entry Fee&quot; to create one.</p>';
  }

  return `
    <div class="entry-fees-grid">
      ${fees.map(fee => {
        // Store fee data in a data attribute to avoid JSON escaping issues in onclick
        const feeDataAttr = encodeURIComponent(JSON.stringify({
          name: fee.description,
          amount: fee.amount,
          currency: fee.currency
        }));
        return `
        <div class="entry-fee-card">
          <div class="entry-fee-card-header">
            <h4>${escapeHtml(fee.description)}</h4>
            <span class="entry-fee-amount">${formatCurrency(fee.amount, fee.currency)}</span>
          </div>
          <div class="entry-fee-actions">
            <button class="btn btn-secondary btn-small" data-fee-data="${feeDataAttr}" onclick="handleEditFeeClick('${tournamentId}', '${fee.id}', this)">Edit</button>
            <button class="btn btn-danger btn-small" onclick="deleteEntryFeeConfirm('${tournamentId}', '${fee.id}')">Delete</button>
          </div>
        </div>
      `;}).join('')}
    </div>
  `;
}

/**
 * Handle edit fee button click - extracts data from button's data attribute
 */
function handleEditFeeClick(tournamentId: string, feeId: string, button: HTMLButtonElement): void {
  const feeDataStr = button.getAttribute('data-fee-data');
  if (feeDataStr) {
    try {
      const feeData = JSON.parse(decodeURIComponent(feeDataStr));
      editEntryFee(tournamentId, feeId, feeData);
    } catch (e) {
      console.error('Failed to parse fee data:', e);
      showError('Failed to load fee data');
    }
  }
}

/**
 * Create entry fees modal HTML
 */
function createEntryFeesModal(): void {
  const modalHTML = `
    <div id="entry-fees-modal" class="modal" style="display: none;">
      <div class="modal-content modal-wide" style="max-width: 600px;">
        <span class="close" onclick="closeEntryFeesModal()">&times;</span>
        <h2 data-i18n="tournament.entryFeesModal.title">Manage Entry Fees</h2>
        <p class="subtitle" data-i18n="tournament.entryFeesModal.subtitle">Configure entry fee options for tournament registration</p>

        <div id="entry-fees-list"></div>

        <div class="modal-actions" style="display: flex; gap: 8px; flex-wrap: wrap;">
          <button type="button" id="add-entry-fee-btn" class="btn btn-primary" data-i18n="tournament.entryFeesModal.addButton">Add Entry Fee</button>
          <button type="button" class="btn btn-neutral" onclick="closeEntryFeesModal()" data-i18n="tournament.entryFeesModal.closeButton">Close</button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', modalHTML);

  // Apply translations to the dynamically created modal
  if (window.i18n && typeof window.i18n.applyTranslations === 'function') {
    setTimeout(() => window.i18n.applyTranslations(), 0);
  }
}

/**
 * Open payment summary modal (uses stored tournament ID from entry fees modal)
 */
export async function openPaymentSummaryModal(): Promise<void> {
  if (!currentTournamentIdForSummary) {
    showError('No tournament selected');
    return;
  }
  await openPaymentSummaryModalFor(currentTournamentIdForSummary);
}

/**
 * Open payment summary modal for a specific tournament
 */
export async function openPaymentSummaryModalFor(tournamentId: string): Promise<void> {
  try {
    const tournamentResponse = await fetch(`${API_BASE_URL}/${tournamentId}`, { credentials: 'include' });
    if (!tournamentResponse.ok) {
      throw new Error('Failed to load tournament data');
    }

    const tournament = await tournamentResponse.json();
    let paymentSummary: PaymentSummary;

    if (tournament.players && tournament.players.length > 0) {
      paymentSummary = await calculatePaymentSummary(tournament.players);
    } else {
      paymentSummary = {
        stripeGross: 0,
        stripeFees: 0,
        nextroundFees: 0,
        stripeNet: 0,
        stripeCount: 0,
        manualTotal: 0,
        manualCount: 0,
        grandTotal: 0,
        currency: 'CHF'
      };
    }

    showPaymentSummaryModal(paymentSummary);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load payment summary';
    showError(message);
  }
}

/**
 * Show payment summary modal
 */
function showPaymentSummaryModal(summary: PaymentSummary): void {
  let modal = document.getElementById('payment-summary-modal');
  if (!modal) {
    createPaymentSummaryModal();
    modal = document.getElementById('payment-summary-modal');
    if (!modal) return;
  }

  const contentEl = modal.querySelector('#payment-summary-content');
  if (contentEl) {
    contentEl.innerHTML = renderPaymentSummary(summary);
  }

  modal.style.display = 'flex';
}

/**
 * Create payment summary modal
 */
function createPaymentSummaryModal(): void {
  const modalHTML = `
    <div id="payment-summary-modal" class="modal" style="display: none;">
      <div class="modal-content" style="max-width: 500px;">
        <span class="close" onclick="closePaymentSummaryModal()">&times;</span>
        <h2>${t('entryFees.paymentSummary', 'Payment Summary')}</h2>
        <div id="payment-summary-content"></div>
        <div class="modal-actions">
          <button type="button" class="btn btn-secondary" onclick="closePaymentSummaryModal()">${t('common.close', 'Close')}</button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', modalHTML);
}

/**
 * Close payment summary modal
 */
export function closePaymentSummaryModal(): void {
  const modal = document.getElementById('payment-summary-modal');
  if (modal) {
    modal.style.display = 'none';
  }
}

/**
 * Close entry fees modal
 */
export function closeEntryFeesModal(): void {
  const modal = document.getElementById('entry-fees-modal');
  if (modal) {
    modal.style.display = 'none';
  }
}

/**
 * Show entry fee form
 */
export function showEntryFeeForm(tournamentId: string, feeId?: string, feeData?: any): void {
  const modal = document.getElementById('entry-fee-form-modal');
  if (!modal) {
    createEntryFeeFormModal();
    return showEntryFeeForm(tournamentId, feeId, feeData);
  }

  // Set form title
  const titleEl = modal.querySelector('#entry-fee-form-title');
  if (titleEl) {
    if (feeId) {
      titleEl.setAttribute('data-i18n', 'tournament.entryFeeForm.titleEdit');
      titleEl.textContent = 'Edit Entry Fee';
    } else {
      titleEl.setAttribute('data-i18n', 'tournament.entryFeeForm.titleAdd');
      titleEl.textContent = 'Add Entry Fee';
    }
    // Apply translations to the updated title
    if (window.i18n && typeof window.i18n.applyTranslations === 'function') {
      setTimeout(() => window.i18n.applyTranslations(), 0);
    }
  }

  // Populate form
  const nameInput = modal.querySelector('#entry-fee-name') as HTMLInputElement;
  const amountInput = modal.querySelector('#entry-fee-amount') as HTMLInputElement;
  const currencySelect = modal.querySelector('#entry-fee-currency') as HTMLSelectElement;
  const descInput = modal.querySelector('#entry-fee-description') as HTMLTextAreaElement;

  if (nameInput) nameInput.value = feeData?.name || '';
  if (amountInput) amountInput.value = feeData ? (feeData.amount / 100).toFixed(2) : '';
  if (currencySelect) currencySelect.value = feeData?.currency || 'EUR';
  if (descInput) descInput.value = feeData?.description || '';

  // Set up submit handler
  const form = modal.querySelector('#entry-fee-form') as HTMLFormElement;
  if (form) {
    form.onsubmit = (e) => {
      e.preventDefault();
      handleEntryFeeSubmit(tournamentId, feeId);
    };
  }

  // Reset button state
  const submitBtn = modal.querySelector('button[type="submit"]') as HTMLButtonElement;
  if (submitBtn) {
    submitBtn.disabled = false;
    submitBtn.textContent = 'Save Entry Fee';
  }

  // Show modal
  modal.style.display = 'flex';
}

/**
 * Create entry fee form modal
 */
function createEntryFeeFormModal(): void {
  const currencyOptions = SUPPORTED_CURRENCIES.map(c =>
    `<option value="${c.code}">${c.code} - ${c.name}</option>`
  ).join('');

  const modalHTML = `
    <div id="entry-fee-form-modal" class="modal" style="display: none;">
      <div class="modal-content">
        <span class="close" onclick="closeEntryFeeForm()">&times;</span>
        <h2 id="entry-fee-form-title" data-i18n="tournament.entryFeeForm.titleAdd">Add Entry Fee</h2>

        <form id="entry-fee-form">
          <div class="form-group">
            <label for="entry-fee-name">${t('entryFees.feeName', 'Fee Name')} *</label>
            <input type="text" id="entry-fee-name" class="form-control" required placeholder="e.g., Regular, Early Bird, Student">
          </div>

          <div class="form-row" style="display: flex; gap: 12px;">
            <div class="form-group" style="flex: 1;">
              <label for="entry-fee-amount">${t('entryFees.amount', 'Amount')} *</label>
              <input type="number" id="entry-fee-amount" class="form-control" required min="0" step="0.01" placeholder="25.00">
            </div>
            <div class="form-group" style="flex: 1;">
              <label for="entry-fee-currency">${t('entryFees.currency', 'Currency')}</label>
              <select id="entry-fee-currency" class="form-control">
                ${currencyOptions}
              </select>
            </div>
          </div>

          <div class="form-group">
            <label for="entry-fee-description">${t('entryFees.description', 'Description')}</label>
            <textarea id="entry-fee-description" class="form-control" rows="2" placeholder="${t('entryFees.descriptionPlaceholder', 'Optional description')}"></textarea>
          </div>

          <div id="entry-fee-form-error" class="error-message"></div>
          <div id="entry-fee-form-success" class="success-message"></div>

          <div class="form-actions">
            <button type="submit" class="btn btn-primary">${t('common.save', 'Save')}</button>
            <button type="button" class="btn btn-secondary" onclick="closeEntryFeeForm()">${t('common.cancel', 'Cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', modalHTML);

  // Apply translations to the dynamically created modal
  if (window.i18n && typeof window.i18n.applyTranslations === 'function') {
    setTimeout(() => window.i18n.applyTranslations(), 0);
  }
}

/**
 * Close entry fee form modal
 */
export function closeEntryFeeForm(): void {
  const modal = document.getElementById('entry-fee-form-modal');
  if (modal) {
    modal.style.display = 'none';

    // Clear form
    const form = modal.querySelector('#entry-fee-form') as HTMLFormElement;
    if (form) form.reset();

    // Clear messages
    const errorEl = modal.querySelector('#entry-fee-form-error') as HTMLElement;
    const successEl = modal.querySelector('#entry-fee-form-success') as HTMLElement;
    if (errorEl) errorEl.textContent = '';
    if (successEl) successEl.textContent = '';

    // Reset button state
    const submitBtn = modal.querySelector('button[type="submit"]') as HTMLButtonElement;
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Save Entry Fee';
    }
  }
}

/**
 * Handle entry fee form submit
 */
async function handleEntryFeeSubmit(tournamentId: string, feeId?: string): Promise<void> {
  const modal = document.getElementById('entry-fee-form-modal');
  if (!modal) return;

  const errorEl = modal.querySelector('#entry-fee-form-error') as HTMLElement;
  const successEl = modal.querySelector('#entry-fee-form-success') as HTMLElement;
  const submitBtn = modal.querySelector('button[type="submit"]') as HTMLButtonElement;

  // Clear messages
  if (errorEl) errorEl.textContent = '';
  if (successEl) successEl.textContent = '';

  // Get form values
  const name = (modal.querySelector('#entry-fee-name') as HTMLInputElement).value.trim();
  const amountStr = (modal.querySelector('#entry-fee-amount') as HTMLInputElement).value;
  const currency = (modal.querySelector('#entry-fee-currency') as HTMLSelectElement).value;
  const description = (modal.querySelector('#entry-fee-description') as HTMLTextAreaElement).value.trim();

  // Validate
  if (!name || !amountStr) {
    if (errorEl) errorEl.textContent = 'Name and amount are required';
    return;
  }

  const amountFloat = parseFloat(amountStr);
  if (isNaN(amountFloat) || amountFloat < 0) {
    if (errorEl) errorEl.textContent = 'Please enter a valid amount';
    return;
  }

  // Convert to cents
  const amount = Math.round(amountFloat * 100);

  // Disable submit button
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.textContent = feeId ? 'Updating...' : 'Creating...';
  }

  try {
    const feeData = { name, amount, currency, description: description || undefined };

    if (feeId) {
      await updateEntryFee(tournamentId, feeId, feeData);
    } else {
      await createEntryFee(tournamentId, feeData);
    }

    // Close form and refresh list
    closeEntryFeeForm();
    const fees = await getEntryFees(tournamentId);
    showEntryFeesModal(tournamentId, fees);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to save entry fee';
    if (errorEl) errorEl.textContent = message;

    // Re-enable submit button
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = feeId ? 'Update Entry Fee' : 'Create Entry Fee';
    }
  }
}

/**
 * Edit entry fee
 */
export function editEntryFee(tournamentId: string, feeId: string, feeData: any): void {
  showEntryFeeForm(tournamentId, feeId, feeData);
}

/**
 * Delete entry fee with confirmation
 */
export async function deleteEntryFeeConfirm(tournamentId: string, feeId: string): Promise<void> {
  try {
    await deleteEntryFee(tournamentId, feeId);

    // Refresh list
    const fees = await getEntryFees(tournamentId);
    showEntryFeesModal(tournamentId, fees);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to delete entry fee';
    showError(message);
  }
}

/**
 * Show error message
 */
function showError(message: string): void {
  if (typeof window.showError === 'function') {
    window.showError(message);
  } else {
    alert('Error: ' + message);
  }
}

// Export to window for onclick handlers
window.openEntryFeesModal = openEntryFeesModal;
window.closeEntryFeesModal = closeEntryFeesModal;
window.showEntryFeeForm = showEntryFeeForm;
window.closeEntryFeeForm = closeEntryFeeForm;
window.editEntryFee = editEntryFee;
window.deleteEntryFeeConfirm = deleteEntryFeeConfirm;
window.openPaymentSummaryModal = openPaymentSummaryModal;
window.openPaymentSummaryModalFor = openPaymentSummaryModalFor;
window.closePaymentSummaryModal = closePaymentSummaryModal;
window.handleEditFeeClick = handleEditFeeClick;
